import {
  Paper,
  Button,
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Fragment, useCallback } from "react";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import { ProjectFile, ProjectState } from "../Project";
import { MainContainer } from "@/shared/structure/MainContainer";
import { TopBar } from "@/shared/structure/TopBar";
import AddFileDialog from "../dialogs/AddFileDialog";
import dayjs from "dayjs";

export default function FilePage({
  project,
  setField,
}: {
  project: ProjectState;
  setField: (changes: Partial<ProjectState>) => void;
}) {
  const deleteFile = useCallback(
    (index: number) => {
      setField({
        files: project.files.slice().map((so, i) => ({
          ...so,
          changed: true,
          deleted: !!so.deleted || i === index,
        })),
      });
    },
    [project]
  );

  const saveFile = useCallback(
    (subOrder: ProjectFile, index: number) => {
      if (index === -1) {
        const files = project.files.slice();
        files.push(subOrder);
        setField({ files });
      } else {
        setField({
          files: project.files
            .slice()
            .map((so, i) =>
              i === index ? { changed: true, ...subOrder } : so
            ),
        });
      }
    },
    [project]
  );

  const fileCount = project.files.filter((x) => !x.deleted).length;

  const sortedFiles = project.files
    .map((x, index) => ({ ...x, index }))
    .filter((x) => !x.deleted)
    .slice()
    .sort((a, b) => {
      const catA = a.category?.toLowerCase() || "";
      const catB = b.category?.toLowerCase() || "";
      const categoryCompare = catA.localeCompare(catB);
      if (categoryCompare !== 0) return categoryCompare;

      const descA = a.description?.toLowerCase() || "";
      const descB = b.description?.toLowerCase() || "";
      return descA.localeCompare(descB);
    });

  let lastCategory: string | null = null;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
      }}
    >
      {project.writeable ? (
        <TopBar>
          <AddFileDialog
            save={(o) => saveFile(o, -1)}
            OpenButton={({ onClick, disabled }) => (
              <Button startIcon={<Add />} onClick={onClick} disabled={disabled}>
                Datei/Link hinzufügen
              </Button>
            )}
            initialState={null}
          />
        </TopBar>
      ) : null}
      <MainContainer paperless>
        {fileCount === 0 ? (
          <Alert severity="info" variant="filled">
            <AlertTitle>Nichts gefunden.</AlertTitle>
            Hier gibt es noch nichts.
          </Alert>
        ) : (
          <Paper>
            {sortedFiles.map((x, i) => {
              const currentCategory = x.category || "";
              const showCategoryHeading = currentCategory !== lastCategory;
              lastCategory = currentCategory;

              return (
                <Fragment key={i}>
                  {showCategoryHeading ? (
                    <Box
                      sx={{
                        padding: "10px 20px",
                        backgroundColor: "#f5f5f5",
                        borderBottom: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontStyle={currentCategory ? "normal" : "italic"}
                      >
                        {currentCategory || "Ohne Kategorie"}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      display: "flex",
                      padding: "10px 20px",
                      alignItems: "center",
                      gap: "20px",
                      borderBottom: "1px solid #aaa",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box sx={{ fontWeight: "bold", minWidth: "200px" }}>
                      {x.description ?? x.originalFilenames?.[x.filename.split(",")[0]] ?? "Keine Beschreibung"}
                    </Box>
                    <Box sx={{ color: "#888", minWidth: "300px" }}>
                      {dayjs
                        .utc(x.createdAt, "YYYY-MM-DD HH:mm:ss")
                        .local()
                        .format("ddd, DD.MM.YYYY HH:mm")}{" "}
                      Uhr
                    </Box>
                    <Box>
                      {x.filename.includes(",") ? (
                        x.filename.split(",").map((m, i) => (
                          <Button
                            href={
                              (import.meta.env.VITE_APP_URL + "/image/") +
                              m
                            }
                            target="_blank"
                            startIcon={<Visibility />}
                          >
                            Datei {i + 1}
                          </Button>
                        ))
                      ) : (
                        <IconButton
                          href={
                            (import.meta.env.VITE_APP_URL + "/image/") +
                            x.filename
                          }
                          target="_blank"
                        >
                          <Visibility />
                        </IconButton>
                      )}
                      {project.writeable ? (
                        <Fragment>
                          <AddFileDialog
                            save={(o) => saveFile(o, x.index)}
                            OpenButton={({ onClick, disabled }) => (
                              <IconButton onClick={onClick} disabled={disabled}>
                                <Edit />
                              </IconButton>
                            )}
                            initialState={x}
                          />
                          <IconButton onClick={() => deleteFile(x.index)}>
                            <Delete />
                          </IconButton>
                        </Fragment>
                      ) : null}
                    </Box>
                  </Box>
                </Fragment>
              );
            })}
          </Paper>
        )}
      </MainContainer>
    </Box>
  );
}
